import { computed, ref, toRaw, type Ref } from 'vue';

interface LimitedHistoryOptions<TSnapshot> {
  limit?: number;
  capture: () => TSnapshot;
  restore: (snapshot: TSnapshot) => void;
}

const DEFAULT_HISTORY_LIMIT = 5;

export function clonePlain<TValue>(value: TValue): TValue {
  try {
    return structuredClone(toRaw(value));
  } catch {
    return JSON.parse(JSON.stringify(value)) as TValue;
  }
}

export function useLimitedHistory<TSnapshot>({
  limit = DEFAULT_HISTORY_LIMIT,
  capture,
  restore,
}: LimitedHistoryOptions<TSnapshot>) {
  const undoHistory = ref<TSnapshot[]>([]) as Ref<TSnapshot[]>;
  const redoHistory = ref<TSnapshot[]>([]) as Ref<TSnapshot[]>;

  const canUndo = computed(() => undoHistory.value.length > 0);
  const canRedo = computed(() => redoHistory.value.length > 0);

  function areSnapshotsEqual(left: TSnapshot, right: TSnapshot): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  function pushLimited(history: TSnapshot[], snapshot: TSnapshot) {
    const previousSnapshot = history[history.length - 1];
    if (previousSnapshot && areSnapshotsEqual(previousSnapshot, snapshot)) {
      return;
    }

    history.push(snapshot);
    if (history.length > limit) {
      history.shift();
    }
  }

  function track(snapshot: TSnapshot = capture()) {
    pushLimited(undoHistory.value, snapshot);
    redoHistory.value = [];
  }

  function reset() {
    undoHistory.value = [];
    redoHistory.value = [];
  }

  function undo() {
    const previousSnapshot = undoHistory.value.pop();
    if (!previousSnapshot) return false;

    pushLimited(redoHistory.value, capture());
    restore(previousSnapshot);
    return true;
  }

  function redo() {
    const nextSnapshot = redoHistory.value.pop();
    if (!nextSnapshot) return false;

    pushLimited(undoHistory.value, capture());
    restore(nextSnapshot);
    return true;
  }

  return {
    canUndo,
    canRedo,
    track,
    reset,
    undo,
    redo,
  };
}
